import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { getDetails } from "../Redux/Action/SubAdmin";
import { AVAILABLE_PERMISSIONS } from "../Config/permissions";

export default function Sidebar() {
  const dispatch = useDispatch();
  const [expandedGroups, setExpandedGroups] = useState({
    'User Management': false,
    'Booking & Finance': true,
    'Content & Settings': false,
    'Documentation': true,
    'System Tools': false
  });

  useEffect(() => {
    dispatch(getDetails());
  }, [dispatch]);

  const admindetails = useSelector((state) => state.SubAdmin.admindetails);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Categorize menu items
  const categorizeMenuItems = () => {
    const seen = new Set();
    
    const categories = {
      'User Management': [],
      'Booking & Finance': [],
      'Content & Settings': [],
      'Documentation': [],
      'System Tools': []
    };

    // Special handling for Admin role - grant all permissions
    if (admindetails?.role === 'Admin') {
      AVAILABLE_PERMISSIONS.forEach(config => {
        if (seen.has(config.url)) return;
        seen.add(config.url);

        const item = {
          url: config.url,
          title: config.title,
          icon: config.iconClass
        };

        const category = config.category || 'Content & Settings';
        categories[category].push(item);
      });
      return categories;
    }

    // Filter out items that are not in permissions
    admindetails?.permission?.forEach((perm) => {
      // Find the corresponding permission configuration
      // Match by URL first, then by title
      const config = AVAILABLE_PERMISSIONS.find(p => p.url === perm.url) || 
                     AVAILABLE_PERMISSIONS.find(p => p.title === perm.title);
      
      if (config) {
        if (seen.has(config.url)) return;
        seen.add(config.url);

        const item = {
          url: config.url,
          title: config.title,
          icon: config.iconClass
        };

        const category = config.category || 'Content & Settings';
        if (categories[category]) {
          categories[category].push(item);
        } else {
          categories['Content & Settings'].push(item);
        }
      }
    });

    return categories;
  };

  const renderMenuItem = (item, index) => {
    return (
      <NavLink
        key={index}
        to={item.url}
        className={({ isActive }) => `
          flex items-center px-3 py-2.5 mx-2 rounded-md text-sm font-medium transition-all duration-200 no-underline
          ${isActive 
            ? 'bg-white/25 text-white shadow-sm border-l-3 border-white' 
            : 'text-emerald-50/90 hover:bg-white/15 hover:text-white hover:translate-x-1'
          }
        `}
      >
        <i className={`${item.icon} w-4 text-center mr-3 opacity-80`}></i>
        <span className="truncate font-medium">{item.title}</span>
      </NavLink>
    );
  };

  const renderCategory = (categoryName, items, icon) => {
    if (!items || items.length === 0) return null;

    const isExpanded = expandedGroups[categoryName];

    return (
      <div key={categoryName} className="mb-4">
        <button
          onClick={() => toggleGroup(categoryName)}
          className="flex items-center justify-between w-full px-3  text-xs font-semibold text-emerald-200 uppercase tracking-wider hover:text-white transition-colors"
        >
          <div className="flex items-center">
            <i className={`${icon} w-4 mr-2 opacity-70`}></i>
            <span>{categoryName}</span>
          </div>
          <svg 
            className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        
        {isExpanded && (
          <div className="mt-2 space-y-1">
            {items.map((item, index) => renderMenuItem(item, index))}
          </div>
        )}
      </div>
    );
  };

  const categories = categorizeMenuItems();

  return (
    <nav className="px-2 ">
      {/* Dashboard Link */}
      <div className="mb-6">
        <NavLink
          to="/dashboard"
          className={({ isActive }) => `
            flex items-center px-3 py-3 mx-2 rounded-lg text-sm font-semibold transition-all duration-200 no-underline
            ${isActive 
              ? 'bg-white/25 text-white shadow-lg' 
              : 'text-emerald-50 hover:bg-white/15 hover:text-white'
            }
          `}
        >
          <i className="fas fa-tachometer-alt w-5 text-center mr-3"></i>
          <span>Dashboard</span>
        </NavLink>
      </div>

      {/* Categorized Menu Items */}
      <div className="space-y-2">
        {renderCategory('User Management', categories['User Management'], 'fas fa-users')}
        {renderCategory('Booking & Finance', categories['Booking & Finance'], 'fas fa-credit-card')}
        {renderCategory('Documentation', categories['Documentation'], 'fas fa-book-open')}
        {renderCategory('Content & Settings', categories['Content & Settings'], 'fas fa-cog')}
        {renderCategory('System Tools', categories['System Tools'], 'fas fa-tools')}
      </div>
    </nav>
  );
}
